import { createContext, PropsWithChildren, useContext, useState } from 'react';
import { FormikErrors } from 'formik';

import { useSuccessPopup } from 'features/my-profile/become-fundraiser/form/components/success/useSuccessPopup';
import { useGlobalError } from 'hooks/useGlobalError';
import { useCurrentUser } from 'providers/cache/cachedDataHooks';
import { userService } from 'services/user/userService';
import { BecomeFundraiserRequestDto, BecomeFundraiserResponseDto, CurrentUserDto } from 'services/user/userService.dto';

import { BecomeFundraiserFormData, useBecomeFundraiserForm } from './useBecomeFundraiserForm';

interface BecomeFundraiserContextType {
  save: () => Promise<void>;
  isSaving: boolean;
  formData: BecomeFundraiserFormData;
  user: CurrentUserDto;
  setFieldValue: (field: string, value: any) => void;
  errors: FormikErrors<BecomeFundraiserFormData>;
  isFormValid: boolean;
  isEditMode: boolean;
}

const BecomeFundraiserContext = createContext<BecomeFundraiserContextType>(null!);

export interface BecomeFundraiserContextProps extends PropsWithChildren {
  user: CurrentUserDto;
  fundraiserData?: BecomeFundraiserResponseDto;
  onSaved?: () => void;
}

const BecomeFundraiserProvider = ({ user, fundraiserData, onSaved, children }: BecomeFundraiserContextProps) => {
  const [isSaving, setIsSaving] = useState(false);
  const { unknownError } = useGlobalError();
  const { showSuccessPopup } = useSuccessPopup();
  const { refreshCurrentUser } = useCurrentUser();

  const save = async (request: BecomeFundraiserRequestDto) => {
    setIsSaving(true);
    try {
      await userService.becomeFundraiser(request);
      refreshCurrentUser(showSuccessPopup);
      onSaved && onSaved();
    } catch (error) {
      unknownError();
    } finally {
      setIsSaving(false);
    }
  };

  const becomeFundraiserForm = useBecomeFundraiserForm(save, fundraiserData);

  return (
    <BecomeFundraiserContext.Provider
      value={{
        save: becomeFundraiserForm.submitForm,
        isSaving,
        formData: becomeFundraiserForm.values,
        user,
        isFormValid: becomeFundraiserForm.isValid && becomeFundraiserForm.dirty && !becomeFundraiserForm.isValidating,
        isEditMode: !!fundraiserData && user.ableToCreateCampaigns,
        ...becomeFundraiserForm,
      }}>
      {children}
    </BecomeFundraiserContext.Provider>
  );
};

const useBecomeFundraiser = () => useContext(BecomeFundraiserContext);

export { BecomeFundraiserProvider, useBecomeFundraiser };
